import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, pointerWithin, useSensor, useSensors,
  type DragEndEvent, type DragMoveEvent, type DragStartEvent,
} from '@dnd-kit/core';
import SiteHeader from '../components/SiteHeader';
import CardPreview from '../components/CardPreview';
import CardImage from '../components/CardImage';
import { NumberPrompt } from '../components/Modals';
import cardBack from '../assets/card-back.jpg';
import { useAuth } from '../auth/AuthContext';
import { duelApi } from '../api/duelApi';
import { decksApi } from '../api/decksApi';
import { useDuelSocket } from '../api/useDuelSocket';
import { useCardImage, ensureMeta, getMeta } from '../api/cardCache';
import { computeLegality, type Legality } from '../deckLegality';
import { cardImageUrl } from '../config';
import {
  buildBoardWithPerm, applyPerm, publicView, isFieldZone, isCounterZone, zoneCard, removeFrom,
  putZone, putPile, slicesFor, sliceAt, snapCenterToCursor, anchorStyle, baseZone, isOppZone, isAnyFieldZone,
  type Board, type Pos, type Inst, type SpinState,
} from '../board/model';
import {
  PlayCard, Zone, PosOption, SliceOverlay, ShuffleFx, OverlayImg, useCountUp, useHalo, HandZone, CounterSource, ViewerStrip, DotMenu, DeckPeekViewer, RevealSelectViewer, StatEditor,
} from '../board/components';
import type { Deck, DeckSummary, DuelClientMsg, DuelServerMsg, DuelStatePlayer, LogSeg } from '../../../shared/types';

const LP_START = 4000;

interface Roster { userId: string; displayName: string; hasDeck: boolean; deckName: string | null; connected: boolean; lp: number; }

export default function DuelPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken, refresh } = useAuth();

  // ── networked state ──
  const [status, setStatus] = useState<'waiting' | 'active' | 'ended'>('waiting');
  const [endedBy, setEndedBy] = useState<string | null>(null);   // who conceded (they lost)
  const [players, setPlayers] = useState<Roster[]>([]);
  const [yourId, setYourId] = useState('');
  const [turn, setTurn] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [oppBoard, setOppBoard] = useState<Board | null>(null);
  const [oppRevealed, setOppRevealed] = useState<Inst[]>([]);
  const [revealVisible, setRevealVisible] = useState(false);
  const [revealSticky, setRevealSticky] = useState(false);
  const [revealNonce, setRevealNonce] = useState(0);
  const [rematchOffer, setRematchOffer] = useState(false);
  const [rematchSent, setRematchSent] = useState(false);
  const [deckPeek, setDeckPeek] = useState<{ cards: Inst[]; reveal: boolean } | null>(null);
  const [revealSel, setRevealSel] = useState<{ n: number; picked: string[] } | null>(null);
  const [incomingDeckReq, setIncomingDeckReq] = useState<{ kind: 'look' | 'reveal'; n: number; by: string } | null>(null);
  const [oppLook, setOppLook] = useState<{ cards: Inst[]; reveal: boolean } | null>(null);
  const [numPrompt, setNumPrompt] = useState<{ title: string; max: number; onSubmit: (n: number) => void } | null>(null);
  const [pendingGive, setPendingGive] = useState<{ card: Inst; dest: string; faceUp: boolean; pos: Pos; orig: string; stage: 'mine' | 'theirs'; plant?: boolean } | null>(null);
  const [incomingGive, setIncomingGive] = useState<{ uid: string; name: string; zone: string; faceUp: boolean; pos: Pos; by: string; plant?: boolean } | null>(null);
  const [giveCountdown, setGiveCountdown] = useState(0);
  const [revealPick, setRevealPick] = useState(false);            // "Reveal card in hand" pick mode
  const [revealConfirm, setRevealConfirm] = useState<Inst | null>(null);   // awaiting the double-check
  const deckPeekRef = useRef<{ cards: Inst[]; reveal: boolean } | null>(null);
  const lastDrawn = useRef<string | null>(null);
  const [shuffleTick, setShuffleTick] = useState(0);
  const shuffleTimer = useRef<number | undefined>(undefined);
  const playShuffleFx = () => { setShuffleTick(t => t + 1); if (shuffleTimer.current) window.clearTimeout(shuffleTimer.current); shuffleTimer.current = window.setTimeout(() => setShuffleTick(0), 520); };
  const [oppShuffleTick, setOppShuffleTick] = useState(0);
  const oppShuffleTimer = useRef<number | undefined>(undefined);
  const playOppShuffleFx = () => { setOppShuffleTick(t => t + 1); if (oppShuffleTimer.current) window.clearTimeout(oppShuffleTimer.current); oppShuffleTimer.current = window.setTimeout(() => setOppShuffleTick(0), 520); };
  const playOppShuffleFxRef = useRef(playOppShuffleFx);
  playOppShuffleFxRef.current = playOppShuffleFx;

  // ── match log (confirmed actions only; synced identically to both players) ──
  const [matchLog, setMatchLog] = useState<{ id: number; segs: LogSeg[] }[]>([]);
  const logId = useRef(0);
  const appendLog = useCallback((segs: LogSeg[]) => setMatchLog(l => [...l.slice(-199), { id: ++logId.current, segs }]), []);
  // Auto-scroll the log's OWN box (never scrollIntoView — that scrolls the page).
  const logScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const el = logScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [matchLog]);

  // ── my board ──
  const [board, setBoard] = useState<Board | null>(null);
  const boardRef = useRef<Board | null>(null);
  const [myDecks, setMyDecks] = useState<DeckSummary[] | null>(null);
  const [chosenDeckId, setChosenDeckId] = useState<string | null>(null);
  const pendingDeck = useRef<Deck | null>(null);

  // ── ui state ──
  const [viewed, setViewed] = useState<string | null>(null);
  const [active, setActive] = useState<{ kind: 'card' | 'counter'; name?: string } | null>(null);
  const [dropHint, setDropHint] = useState<{ zone: string; slice: number } | null>(null);
  const [prevPos, setPrevPos] = useState<{ zone: string; slice: number } | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [statEdit, setStatEdit] = useState<{ zone: string; name: string; atk?: number; def?: number } | null>(null);
  const [viewer, setViewer] = useState<{ side: 'me' | 'opp'; pile: 'deck' | 'gy' | 'extra' | 'banished' } | null>(null);
  const [lpAmt, setLpAmt] = useState('');
  const [dice, setDice] = useState<number | null>(null);
  const [coin, setCoin] = useState<'Heads' | 'Tails' | null>(null);
  const [diceState, setDiceState] = useState<SpinState>('idle');
  const [coinState, setCoinState] = useState<SpinState>('idle');
  const spinTimers = useRef<number[]>([]);
  useEffect(() => () => { spinTimers.current.forEach(id => window.clearInterval(id)); }, []);
  const [confirmConcede, setConfirmConcede] = useState(false);
  const [copied, setCopied] = useState(false);
  const startPointer = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/duel/${id}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { state: { from: `/duel/${id}` }, replace: true });
  }, [authLoading, user, id, navigate]);

  // Load lobby + my decks once.
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    duelApi.get(id, getToken, refresh)
      .then(l => { if (!cancelled) { setPlayers(l.players.map(p => ({ ...p, lp: LP_START }))); setYourId(l.yourId); setStatus(l.status); } })
      .catch(e => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Duel not found'); });
    decksApi.listMine(getToken, refresh).then(d => { if (!cancelled) setMyDecks(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id, authLoading, user, getToken, refresh]);

  const { haloed, flash } = useHalo();
  const setMyBoard = (b: Board | null) => { boardRef.current = b; setBoard(b); };

  const applyState = useCallback((p: DuelStatePlayer[], you: string) => {
    setPlayers(p.map(sp => ({
      userId: sp.userId, displayName: sp.displayName, hasDeck: sp.hasDeck,
      deckName: sp.deckName, connected: sp.connected, lp: sp.lp,
    })));
    setYourId(you);
  }, []);

  // ── socket ──
  // Refs so the memoized onMessage can see the latest roster + send fn.
  const playersRef = useRef<Roster[]>([]);
  playersRef.current = players;
  const sendRef = useRef<((m: DuelClientMsg) => void) | null>(null);
  const onMessage = useCallback((msg: DuelServerMsg) => {
    switch (msg.type) {
      case 'state': {
        setStatus(msg.status);
        setTurn(msg.turn);
        setYourId(msg.yourId);
        applyState(msg.players, msg.yourId);
        if (msg.oppPublic) setOppBoard(msg.oppPublic as Board);
        if (msg.yourPrivate) setMyBoard(msg.yourPrivate as Board);
        // A page refresh wipes React state + refs (chosenDeckId / pendingDeck),
        // so restore the chosen deck from the server snapshot and prefetch it —
        // otherwise a later rematch has nothing to rebuild the board from, the
        // board never resets, and the opponent sees this player's cards vanish.
        if (msg.yourDeckId) {
          setChosenDeckId(msg.yourDeckId);
          if (!pendingDeck.current) decksApi.get(msg.yourDeckId, getToken, refresh).then(d => { pendingDeck.current = d; }).catch(() => {});
        }
        break;
      }
      case 'presence':
        setPlayers(prev => prev.map(p => p.userId === msg.userId ? { ...p, connected: msg.connected } : p));
        // If we joined before this player did, our roster doesn't have them yet
        // (presence only updates existing rows) — re-join to get the full state,
        // otherwise their LP/name never sync.
        if (!playersRef.current.some(p => p.userId === msg.userId)) sendRef.current?.({ action: 'join' });
        break;
      case 'deckReady': {
        const d = pendingDeck.current;
        if (d) { const nb = buildBoardWithPerm(d, msg.perm); setMyBoard(nb); sendSyncRef.current(nb); }
        break;
      }
      case 'oppDeck':
        setPlayers(prev => prev.map(p => p.userId !== yourId ? { ...p, hasDeck: true, deckName: msg.deckName } : p));
        break;
      case 'start':
        setStatus('active');
        setTurn(msg.turn);
        break;
      case 'oppSync':
        setOppBoard(msg.public as Board);
        break;
      case 'revealPing':
        setOppRevealed(msg.cards as Inst[]);
        setRevealSticky(msg.sticky);
        setRevealNonce(n => n + 1);
        break;
      case 'oppLp':
        setPlayers(prev => prev.map(p => p.userId === msg.userId ? { ...p, lp: msg.value } : p));
        break;
      case 'perm': {
        const cur = boardRef.current;
        if (cur) { const nb = { ...cur, deck: applyPerm(cur.deck, msg.perm) }; setMyBoard(nb); sendSyncRef.current(nb); playShuffleFxRef.current(); }
        break;
      }
      case 'oppShuffle':
        playOppShuffleFxRef.current();
        break;
      case 'rng':
        // Both clients play the build-up + land on the server value + lock.
        startSpinRef.current(msg.kind, msg.value, msg.by);
        break;
      case 'log':
        appendLog(msg.segs);
        break;
      case 'deckReq':
        setIncomingDeckReq({ kind: msg.kind, n: msg.n, by: msg.by });
        break;
      case 'deckResp':
        handleDeckRespRef.current(msg.kind, msg.accept, msg.cards);
        break;
      case 'turn':
        setTurn(msg.turn);
        break;
      case 'ended':
        setStatus('ended');
        setEndedBy(msg.by);   // the conceder — everyone else won
        break;
      case 'rematchOffer':
        setRematchOffer(true);
        break;
      case 'rematchDeclined':
        setRematchSent(false);
        navigate('/duel');   // opponent declined → back out to the duel home
        break;
      case 'rematchStart': {
        // Fresh game: wipe EVERY per-game bit of state and rebuild the board from
        // scratch out of the chosen deck (so given/plant/moved cards are all back).
        setStatus('active');
        setEndedBy(null);
        setTurn(msg.turn);
        setRematchOffer(false); setRematchSent(false);
        setDice(null); setCoin(null); setDiceState('idle'); setCoinState('idle');
        setOppRevealed([]); setRevealVisible(false); setRevealSticky(false);
        setDeckPeek(null); setRevealSel(null); setOppLook(null); setIncomingDeckReq(null); setPendingGive(null); setIncomingGive(null); setStatEdit(null);
        setViewer(null); setMenuFor(null); setConfirmConcede(false); setLpAmt(''); setError('');
        setRevealPick(false); setRevealConfirm(null);
        deckPeekRef.current = null; lastDrawn.current = null;
        setOppBoard(null);
        setMatchLog([]); logId.current = 0;
        setPlayers(prev => prev.map(p => ({ ...p, lp: LP_START })));
        const perm = msg.perm;
        // Rebuild my board, sync it, then RE-sync shortly after. The re-sync is the
        // race guard: my `setOppBoard(null)` above and the opponent's first post-reset
        // sync can be delivered out of order (separate Lambda invocations), which would
        // wipe their freshly-synced board for good. A second broadcast — sent once both
        // sides have finished clearing — guarantees each opponent board repopulates.
        const rebuild = (deck: Deck) => {
          const nb = buildBoardWithPerm(deck, perm);
          setMyBoard(nb); sendSyncRef.current(nb);
          window.setTimeout(() => { const cur = boardRef.current; if (cur) sendSyncRef.current(cur); }, 400);
        };
        if (pendingDeck.current) rebuild(pendingDeck.current);
        else if (chosenDeckId) decksApi.get(chosenDeckId, getToken, refresh).then(d => { pendingDeck.current = d; rebuild(d); }).catch(() => {});
        break;
      }
      case 'halo':
        // Sender's `mine` is their perspective → flip for us (their opponent's card).
        flash(`${msg.mine ? 'opp' : 'mine'}:${msg.uid}`);
        break;
      case 'giveOffer':
        setIncomingGive({ ...msg.give, by: msg.by });
        break;
      case 'giveResult':
        handleGiveResultRef.current(msg.uid, msg.accept);
        break;
      case 'error':
        setError(msg.message);
        break;
    }
  }, [applyState, yourId, flash, chosenDeckId, getToken, refresh, appendLog]);

  const { status: sockStatus, send } = useDuelSocket(id, { enabled: !!user && !loadError, getToken, refresh, onMessage });
  sendRef.current = send;

  // (Re)join whenever the socket (re)opens to sync state.
  useEffect(() => { if (sockStatus === 'open') send({ action: 'join' }); }, [sockStatus, send]);

  // A give offer auto-declines after 10s (with a visible countdown).
  useEffect(() => {
    if (!incomingGive) { setGiveCountdown(0); return; }
    setGiveCountdown(10);
    const iv = window.setInterval(() => setGiveCountdown(c => { if (c <= 1) { window.clearInterval(iv); declineIncomingRef.current(); return 0; } return c - 1; }), 1000);
    return () => window.clearInterval(iv);
  }, [incomingGive]);

  // The opponent-revealed strip. A hand reveal fades after 4s; a deck "Reveal
  // Top N" stays (sticky) until an empty ping. Re-triggers on every ping.
  useEffect(() => {
    if (!oppRevealed.length) { setRevealVisible(false); return; }
    setRevealVisible(true);
    if (revealSticky) return;
    const t = window.setTimeout(() => setRevealVisible(false), 4000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce]);

  const sendSync = useCallback((b: Board) => { send({ action: 'sync', public: publicView(b), private: b }); }, [send]);
  const sendSyncRef = useRef(sendSync);
  sendSyncRef.current = sendSync;
  const playShuffleFxRef = useRef(playShuffleFx);
  playShuffleFxRef.current = playShuffleFx;
  const revealPing = (cards: Inst[], sticky: boolean) => send({ action: 'revealPing', cards: cards.map(c => ({ uid: c.uid, name: c.name })), sticky });

  // A board mutation that also broadcasts the redacted public view.
  const mutate = (fn: (b: Board) => Board) => {
    const cur = boardRef.current;
    if (!cur) return;
    const nb = fn(cur);
    setMyBoard(nb);
    sendSync(nb);
  };

  const myTurn = turn === yourId;
  const me = players.find(p => p.userId === yourId);
  const opp = players.find(p => p.userId !== yourId);
  const shownMyLp = useCountUp(me?.lp ?? LP_START);
  const shownOppLp = useCountUp(opp?.lp ?? LP_START);

  // ── match-log helpers ──
  // pushLog: append locally AND broadcast (the opponent appends on the relay).
  const myName = me?.displayName ?? 'You';
  const oppName = opp?.displayName ?? 'Opponent';
  const pushLog = (segs: LogSeg[]) => { appendLog(segs); send({ action: 'log', segs }); };
  const nameSeg = (name: string, visible: boolean): LogSeg => (visible ? { card: name } : 'a card');
  const posWord = (faceUp: boolean, pos: Pos) => `${faceUp ? 'face-up' : 'set'} ${pos.toUpperCase()}`;
  const zoneName = (z: string) => (z.startsWith('st-') ? 'Spell/Trap Zone' : z === 'fieldspell' ? 'Field Zone' : z === 'skill' ? 'Skill Zone' : 'field');
  const joinCards = (cards: { name: string }[]): LogSeg[] => cards.flatMap((c, i) => (i === 0 ? [{ card: c.name }] : [', ', { card: c.name }]));
  // The catalog name of a card by uid, whether it lives in a field zone or a pile.
  const instName = (b: Board, src: string, uid: string): string => {
    if (isFieldZone(src)) { const c = zoneCard(b, src); return c?.uid === uid ? c.name : ''; }
    const arr = (b as unknown as Record<string, Inst[]>)[src];
    const c = Array.isArray(arr) ? arr.find(x => x.uid === uid) : null;
    return c?.name ?? '';
  };

  // ── deck select ──
  async function chooseDeck(deckId: string) {
    setChosenDeckId(deckId);
    try {
      const deck = await decksApi.get(deckId, getToken, refresh);
      pendingDeck.current = deck;
      send({ action: 'selectDeck', deckId });
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load deck'); setChosenDeckId(null); }
  }

  // ── actions ──
  const draw = () => {
    if (deckPeek) return;
    const b = boardRef.current; if (!b || !b.deck.length) return;
    const c = b.deck[0];
    lastDrawn.current = c.uid;
    // A "planted" card is auto-revealed to the opponent + haloed the moment it's drawn.
    if (c.plant) { flash(`mine:${c.uid}`); revealPing([{ uid: c.uid, name: c.name }], false); }
    const nb = { ...b, deck: b.deck.slice(1), hand: [...b.hand, { uid: c.uid, name: c.name }] };
    setMyBoard(nb); sendSync(nb);
    pushLog([myName, ' drew 1 card']);
  };
  const excavate = () => {
    if (deckPeek) return;
    const b = boardRef.current; if (!b || !b.deck.length) return;
    const c = b.deck[0];
    mutate(bb => ({ ...bb, deck: bb.deck.slice(1), gy: [...bb.gy, bb.deck[0]] }));
    pushLog([myName, ' sent ', { card: c.name }, ' from the top of their deck to the GY']);
  };
  // Hand ⋯ actions. Reveal marks cards for the opponent (sticky until they leave the hand).
  const discardRandom = () => {
    const b = boardRef.current; if (!b || !b.hand.length) return;
    const i = Math.floor(Math.random() * b.hand.length);
    const c = b.hand[i];
    mutate(bb => ({ ...bb, hand: bb.hand.filter((_, j) => j !== i), gy: [...bb.gy, bb.hand[i]] }));
    pushLog([myName, ' discarded ', { card: c.name }, ' at random']);
  };
  const reveal = (uids: string[], random = false) => {
    uids.forEach(u => flash(`mine:${u}`));                 // halo my own hand card(s) for feedback
    const b = boardRef.current; if (!b) return;
    const cards = b.hand.filter(c => uids.includes(c.uid));
    if (!cards.length) return;
    revealPing(cards, false);
    if (cards.length === 1) pushLog([myName, ' revealed ', { card: cards[0].name }, ' from their hand', random ? ' at random' : '']);
    else pushLog([myName, ' revealed their hand: ', ...joinCards(cards)]);
  };
  // Deck ⋯ actions: reveal/look at top N, reorder, return in order. Draw-locked.
  // A custom "how many?" popup (never the browser prompt).
  const promptN = (title: string, max: number, onSubmit: (n: number) => void) => { if (max > 0) setNumPrompt({ title, max, onSubmit }); };
  const openPeek = (revealMode: boolean) => {
    const b0 = boardRef.current; if (!b0 || !b0.deck.length) return;
    promptN('How many cards from the top?', b0.deck.length, (n) => {
      const b = boardRef.current; if (!b || !b.deck.length) return;
      const dp = { cards: b.deck.slice(0, n).map(c => ({ uid: c.uid, name: c.name })), reveal: revealMode };
      deckPeekRef.current = dp; setDeckPeek(dp);
      const plural = n === 1 ? ' card' : ' cards';
      if (revealMode) { revealPing(dp.cards, true); pushLog([myName, ' revealed the top ', String(n), plural, ' of their deck: ', ...joinCards(dp.cards)]); }
      else pushLog([myName, ' looked at the top ', String(n), plural, ' of their deck']);
    });
  };
  const peekReorder = (from: number, to: number) => { const dp = deckPeekRef.current; if (!dp) return; const cards = [...dp.cards]; const [m] = cards.splice(from, 1); cards.splice(to, 0, m); const nd = { ...dp, cards }; deckPeekRef.current = nd; setDeckPeek(nd); if (nd.reveal) revealPing(nd.cards, true); };
  const peekClose = () => { const dp = deckPeekRef.current; deckPeekRef.current = null; setDeckPeek(null); if (dp) { if (dp.reveal) revealPing([], false); mutate(b => ({ ...b, deck: [...dp.cards, ...b.deck.slice(dp.cards.length)] })); } };
  // "Reveal Any N Cards": browse the whole deck, click-select up to N, ✓ to reveal.
  const openRevealAny = () => { const b = boardRef.current; if (!b || !b.deck.length) return; promptN('How many cards to reveal?', b.deck.length, (n) => setRevealSel({ n, picked: [] })); };
  const toggleRevealPick = (uid: string) => setRevealSel(s => { if (!s) return s; const has = s.picked.includes(uid); const picked = has ? s.picked.filter(u => u !== uid) : (s.picked.length < s.n ? [...s.picked, uid] : s.picked); return { ...s, picked }; });
  // Browsing the whole deck exposes its order, so shuffle when this closes (either
  // way) — unlike the top-N peeks, which preserve order.
  const closeRevealAny = () => { setRevealSel(null); shuffleDeck(); };
  const confirmRevealAny = () => {
    const s = revealSel; const b = boardRef.current; if (!s || !b) { closeRevealAny(); return; }
    const cards = b.deck.filter(c => s.picked.includes(c.uid));
    if (cards.length) { revealPing(cards, false); pushLog([myName, ' revealed ', ...joinCards(cards), ' from their deck']); }
    closeRevealAny();
  };
  const handMenu = [
    { label: 'Discard random card', onClick: discardRandom },
    { label: 'Reveal card in hand', onClick: () => { setRevealConfirm(null); setRevealPick(true); } },
    { label: 'Reveal random card', onClick: () => { const b = boardRef.current; if (b && b.hand.length) reveal([b.hand[Math.floor(Math.random() * b.hand.length)].uid], true); } },
    { label: 'Reveal last drawn', onClick: () => { if (lastDrawn.current) reveal([lastDrawn.current]); } },
    { label: 'Reveal hand', onClick: () => { const b = boardRef.current; if (b) reveal(b.hand.map(c => c.uid)); } },
  ];
  const deckMenu = [
    { label: 'Reveal Any N Cards', onClick: openRevealAny },
    { label: 'Reveal Top N Cards', onClick: () => openPeek(true) },
    { label: 'Look at Top N Cards', onClick: () => openPeek(false) },
  ];
  // ── acting on the OPPONENT's deck (they must confirm first) ──
  const reqOppDeck = (kind: 'look' | 'reveal') => {
    const max = oppBoard?.deck.length ?? 0; if (!max) return;
    promptN(kind === 'look' ? 'Look at how many of their top cards?' : 'Reveal how many of their top cards?', max, (n) => send({ action: 'deckReq', kind, n }));
  };
  const oppDeckMenu = [
    { label: 'Reveal Top N Cards', onClick: () => reqOppDeck('reveal') },
    { label: 'Look at Top N Cards', onClick: () => reqOppDeck('look') },
  ];
  const acceptDeckReq = () => {
    const r = incomingDeckReq; const b = boardRef.current; if (!r || !b) { setIncomingDeckReq(null); return; }
    const cards = b.deck.slice(0, r.n).map(c => ({ uid: c.uid, name: c.name }));
    send({ action: 'deckResp', kind: r.kind, accept: true, cards });
    setIncomingDeckReq(null);
  };
  const declineDeckReq = () => { const r = incomingDeckReq; if (!r) return; send({ action: 'deckResp', kind: r.kind, accept: false }); setIncomingDeckReq(null); };
  const handleDeckResp = (kind: 'look' | 'reveal', accept: boolean, cards?: { uid: string; name: string }[]) => {
    if (!accept) { setError(`${oppName} declined.`); window.setTimeout(() => setError(''), 3000); return; }
    const cs = (cards ?? []) as Inst[];
    setOppLook({ cards: cs, reveal: kind === 'reveal' });
    const plural = cs.length === 1 ? ' card' : ' cards';
    if (kind === 'reveal') pushLog([myName, ' revealed the top ', String(cs.length), plural, ' of ', oppName, "'s deck: ", ...joinCards(cs)]);
    else pushLog([myName, ' looked at the top ', String(cs.length), plural, ' of ', oppName, "'s deck"]);
  };
  const handleDeckRespRef = useRef(handleDeckResp);
  handleDeckRespRef.current = handleDeckResp;

  // ── giving a card to the opponent (two-step confirm handshake) ──
  const putBack = (card: Inst, zone: string) => mutate(b =>
    isFieldZone(zone)
      ? (zoneCard(b, zone) ? putPile(b, 'hand', card) : putZone(b, zone, { ...card, faceUp: true, pos: 'atk' }))
      : putPile(b, zone, card));
  const startGive = (uid: string, orig: string, dest: string, faceUp: boolean, pos: Pos, plant = false) => {
    const b = boardRef.current; if (!b) return;
    const [nb, inst] = removeFrom(b, orig, uid);
    if (!inst) return;
    setMyBoard(nb); sendSync(nb);                        // the card leaves my board while offered
    setPendingGive({ card: inst, dest, faceUp, pos, orig, stage: 'mine', plant });
  };
  const confirmGive = () => { const pg = pendingGive; if (!pg) return; setPendingGive({ ...pg, stage: 'theirs' }); send({ action: 'giveOffer', give: { uid: pg.card.uid, name: pg.card.name, zone: pg.dest, faceUp: pg.faceUp, pos: pg.pos, plant: pg.plant } }); };
  const cancelGive = () => { const pg = pendingGive; if (!pg) return; putBack(pg.card, pg.orig); setPendingGive(null); };
  const handleGiveResult = (uid: string, accept: boolean) => {
    const pg = pendingGive; if (!pg || pg.card.uid !== uid) return;
    if (!accept) putBack(pg.card, pg.orig);             // declined → comes home
    else if (pg.plant) pushLog([myName, ' shuffled ', { card: pg.card.name }, ' face-up into ', oppName, "'s deck"]);
    else pushLog([myName, ' gave ', oppName, ' ', nameSeg(pg.card.name, pg.faceUp)]);
    setPendingGive(null);                                // accepted → now on their board via oppSync
  };
  const handleGiveResultRef = useRef(handleGiveResult);
  handleGiveResultRef.current = handleGiveResult;
  const acceptIncoming = () => {
    const ig = incomingGive; if (!ig) return;
    mutate(b => {
      if (ig.plant) {                                    // shuffle face-up into my deck at a random spot
        const at = Math.floor(Math.random() * (b.deck.length + 1));
        const deck = [...b.deck]; deck.splice(at, 0, { uid: ig.uid, name: ig.name, plant: true });
        return { ...b, deck };
      }
      return isFieldZone(ig.zone)
        ? (zoneCard(b, ig.zone) ? putPile(b, 'hand', { uid: ig.uid, name: ig.name }) : putZone(b, ig.zone, { uid: ig.uid, name: ig.name, faceUp: ig.faceUp, pos: ig.pos }))
        : putPile(b, ig.zone, { uid: ig.uid, name: ig.name });
    });
    send({ action: 'giveResult', uid: ig.uid, accept: true });
    setIncomingGive(null);
  };
  const declineIncoming = () => { const ig = incomingGive; if (!ig) return; send({ action: 'giveResult', uid: ig.uid, accept: false }); setIncomingGive(null); };
  const declineIncomingRef = useRef(declineIncoming);
  declineIncomingRef.current = declineIncoming;
  const shuffleDeck = () => { const b = boardRef.current; if (b && b.deck.length > 1) { pushLog([myName, ' shuffled their deck']); send({ action: 'rng', kind: 'shuffle', n: b.deck.length }); } };
  // Leaving your own deck view shuffles it (peeked order can't be exploited).
  const changeViewer = (next: typeof viewer) => {
    const leavingMyDeck = viewer?.side === 'me' && viewer.pile === 'deck' && !(next?.side === 'me' && next.pile === 'deck');
    if (leavingMyDeck) shuffleDeck();
    setViewer(next);
  };
  const toggleViewer = (side: 'me' | 'opp', pile: 'deck' | 'gy' | 'extra' | 'banished') =>
    changeViewer(viewer && viewer.side === side && viewer.pile === pile ? null : { side, pile });
  const viewerCards = viewer ? (((viewer.side === 'me' ? board : oppBoard)?.[viewer.pile]) ?? []) : [];
  const setPos = (loc: string, faceUp: boolean, pos: Pos) => {
    const b = boardRef.current; const c = b ? zoneCard(b, loc) : null;
    mutate(bb => { const cc = zoneCard(bb, loc); return cc ? putZone(bb, loc, { ...cc, faceUp, pos }) : bb; });
    if (c && (c.faceUp !== faceUp || c.pos !== pos))
      pushLog([myName, ' changed ', nameSeg(c.name, faceUp || c.faceUp), ' from ', posWord(c.faceUp, c.pos), ' to ', posWord(faceUp, pos)]);
  };
  const placeCard = (uid: string, src: string, dest: string, faceUp: boolean, pos: Pos) => {
    const b = boardRef.current; const name = b ? instName(b, src, uid) : '';
    mutate(bb => { const sc = isFieldZone(src) ? zoneCard(bb, src) : null; const [b2, inst] = removeFrom(bb, src, uid); return inst ? putZone(b2, dest, { ...inst, faceUp, pos, counters: sc?.counters, atk: sc?.atk, def: sc?.def }) : bb; });
    if (name) {
      if (!faceUp) pushLog([myName, ' set a card']);
      else if (dest.startsWith('mon-')) pushLog([myName, ' played ', { card: name }, ' in ', pos === 'atk' ? 'attack' : 'defense', ' position']);
      else pushLog([myName, ' played ', { card: name }, ' in the ', zoneName(dest)]);
    }
  };
  // A card fetched from the deck via the viewer (a search): revealed + logged by name.
  const searchToHand = (uid: string) => {
    const b = boardRef.current; const name = b ? instName(b, 'deck', uid) : '';
    mutate(bb => { const [b2, inst] = removeFrom(bb, 'deck', uid); return inst ? putPile(b2, 'hand', inst) : bb; });
    if (name) { revealPing([{ uid, name }], false); pushLog([myName, ' added ', { card: name }, ' from their deck to hand']); }
  };
  const setStats = (zone: string, atk: number, def: number) => {
    const b = boardRef.current; const c = b ? zoneCard(b, zone) : null;
    mutate(bb => { const cc = zoneCard(bb, zone); return cc ? putZone(bb, zone, { ...cc, atk, def }) : bb; });
    if (c) pushLog([myName, ' set ', { card: c.name }, "'s stats to ", String(atk), '/', String(def)]);
  };
  const pileMove = (uid: string, src: string, dest: string) => {
    const b = boardRef.current; const name = b ? instName(b, src, uid) : '';
    mutate(bb => { const [b2, inst] = removeFrom(bb, src, uid); return inst ? putPile(b2, dest, inst) : bb; });
    if (!name) return;
    if (dest === 'gy') pushLog([myName, ' sent ', { card: name }, isFieldZone(src) ? ' from the field to the GY' : ' to the GY']);
    else if (dest === 'banished') pushLog([myName, ' banished ', { card: name }]);
    else if (dest === 'hand') pushLog([myName, ' returned a card to their hand']);
    else if (dest === 'extra') pushLog([myName, ' returned a card to their Extra Deck']);
  };
  const deckMove = (uid: string, src: string, toTop: boolean) => {
    mutate(bb => { const [b2, inst] = removeFrom(bb, src, uid); return inst ? { ...b2, deck: toTop ? [inst, ...b2.deck] : [...b2.deck, inst] } : bb; });
    pushLog([myName, ' put a card on ', toTop ? 'top' : 'the bottom', ' of their deck']);
  };
  // Drop onto the deck's "Shuffle" slice: the card goes into the deck and the
  // whole deck is reshuffled (server RNG → both sides see the riffle animation).
  const shuffleInto = (uid: string, src: string) => {
    const b = boardRef.current; const name = b ? instName(b, src, uid) : '';
    mutate(bb => { const [b2, inst] = removeFrom(bb, src, uid); return inst ? { ...b2, deck: [...b2.deck, inst] } : bb; });
    const nb = boardRef.current;
    if (nb && nb.deck.length > 1) send({ action: 'rng', kind: 'shuffle', n: nb.deck.length });
    else playShuffleFx();
    if (name) pushLog([myName, ' shuffled ', { card: name }, ' into their deck']);
  };
  const addCounter = (zone: string) => {
    const b = boardRef.current; const c = b ? zoneCard(b, zone) : null;
    mutate(bb => { const cc = zoneCard(bb, zone); return cc ? putZone(bb, zone, { ...cc, counters: (cc.counters ?? 0) + 1 }) : bb; });
    if (c) pushLog([myName, ' added a counter to ', nameSeg(c.name, c.faceUp)]);
  };
  const removeCounter = (zone: string) => {
    const b = boardRef.current; const c = b ? zoneCard(b, zone) : null;
    mutate(bb => { const cc = zoneCard(bb, zone); return cc ? putZone(bb, zone, { ...cc, counters: Math.max(0, (cc.counters ?? 0) - 1) }) : bb; });
    if (c && (c.counters ?? 0) > 0) pushLog([myName, ' removed a counter from ', nameSeg(c.name, c.faceUp)]);
  };
  const moveCounter = (src: string, dest: string) => {
    const b0 = boardRef.current; const sc0 = b0 ? zoneCard(b0, src) : null;
    mutate(b => {
      const sc = zoneCard(b, src);
      if (!sc || (sc.counters ?? 0) <= 0) return b;
      let nb = putZone(b, src, { ...sc, counters: (sc.counters ?? 0) - 1 });
      const dc = zoneCard(nb, dest);
      if (dc) nb = putZone(nb, dest, { ...dc, counters: (dc.counters ?? 0) + 1 });
      return nb;
    });
    if (sc0 && (sc0.counters ?? 0) > 0) pushLog([myName, ' moved a counter']);
  };

  // The click just locks the button and asks the server; BOTH clients then play
  // the identical build-up + result when the `rng` broadcast arrives.
  // Two-log rule so results never spoil: "rolled a die" logs on click; the value
  // ("rolled a 3") only after the ~1s animation lands (pushed by the roller).
  const rollDice = () => { if (diceState !== 'idle') return; setDiceState('rolling'); pushLog([myName, ' rolled a die']); send({ action: 'rng', kind: 'dice' }); };
  const flipCoin = () => { if (coinState !== 'idle') return; setCoinState('rolling'); pushLog([myName, ' flipped a coin']); send({ action: 'rng', kind: 'coin' }); };
  // ~1s of ticking that lands on the server result, then a 250ms lock. Runs on
  // both clients (triggered by the broadcast), so both animate and stay locked.
  // `by` is the roller — only they log the result line (once, after the reveal).
  const startSpin = (kind: 'dice' | 'coin', value: number | string, by: string) => {
    const setState = kind === 'dice' ? setDiceState : setCoinState;
    setState('rolling');
    let i = 0, prevDice = 0, side = false;
    const iv = window.setInterval(() => {
      if (i >= 18) {
        window.clearInterval(iv);
        if (kind === 'dice') setDice(Number(value)); else setCoin(value as 'Heads' | 'Tails');
        setState('cooldown');
        if (by === yourId) pushLog(kind === 'dice' ? [myName, ' rolled a ', String(value)] : [myName, ' flipped ', String(value)]);
        spinTimers.current.push(window.setTimeout(() => setState('idle'), 250));
        return;
      }
      if (kind === 'dice') { let v = 1 + Math.floor(Math.random() * 6); while (v === prevDice) v = 1 + Math.floor(Math.random() * 6); prevDice = v; setDice(v); }
      else { side = !side; setCoin(side ? 'Heads' : 'Tails'); }
      i++;
    }, 50);
    spinTimers.current.push(iv);
  };
  const startSpinRef = useRef(startSpin);
  startSpinRef.current = startSpin;

  const setMyLp = (value: number) => {
    setPlayers(prev => prev.map(p => p.userId === yourId ? { ...p, lp: Math.max(0, value) } : p));
    send({ action: 'lp', value: Math.max(0, value) });
  };
  const applyLp = (sign: 1 | -1) => {
    const n = Math.abs(parseInt(lpAmt, 10) || 0);
    if (!n || !me) return;
    const nv = Math.max(0, me.lp + sign * n);
    if (nv === me.lp) return;
    setMyLp(nv);
    pushLog([myName, nv < me.lp ? ' lost ' : ' gained ', String(Math.abs(nv - me.lp)), ' LP (', String(me.lp), ' → ', String(nv), ')']);
  };
  const endTurn = () => { pushLog([myName, ' ended their turn']); send({ action: 'endTurn' }); };
  const concede = () => { pushLog([myName, ' conceded']); send({ action: 'concede' }); setConfirmConcede(false); };

  const pointerFrom = (delta: { x: number; y: number }) => ({ x: startPointer.current.x + delta.x, y: startPointer.current.y + delta.y });

  function onDragStart(e: DragStartEvent) {
    const ev = e.activatorEvent as MouseEvent & TouchEvent;
    if (ev.touches && ev.touches[0]) startPointer.current = { x: ev.touches[0].clientX, y: ev.touches[0].clientY };
    else startPointer.current = { x: ev.clientX ?? 0, y: ev.clientY ?? 0 };
    const kind = e.active.data.current?.kind as string | undefined;
    if (kind === 'counter-add' || kind === 'counter-move') { setActive({ kind: 'counter' }); setPrevPos(null); }
    else {
      setActive({ kind: 'card', name: e.active.data.current?.name as string });
      const src = e.active.data.current?.src as string;
      const b = boardRef.current;
      const c = b && isFieldZone(src) ? zoneCard(b, src) : null;
      setPrevPos(c ? { zone: src, slice: slicesFor(src).findIndex(s => s.faceUp === c.faceUp && s.pos === c.pos) } : null);
    }
    setMenuFor(null);
    document.body.classList.add('dnd-dragging');
  }
  function onDragMove(e: DragMoveEvent) {
    const kind = e.active.data.current?.kind as string | undefined;
    if (kind === 'counter-add' || kind === 'counter-move') { setDropHint(h => (h ? null : h)); return; }
    const src = e.active.data.current?.src as string;
    const overId = String(e.over?.id ?? '');
    const b = boardRef.current;
    // Own empty field zone / own zone re-pos / the deck (top/bottom) / OR any
    // opponent field zone (giving a card there — pick a position).
    const overOppField = isOppZone(overId) && isAnyFieldZone(overId);
    if (b && (overId === 'deck' || overId === 'opp-deck' || overOppField || (isFieldZone(overId) && (!zoneCard(b, overId) || overId === src)))) {
      const p = pointerFrom(e.delta);
      const q = sliceAt(overId, p.x, p.y);
      setDropHint(h => (h && h.zone === overId && h.slice === q ? h : { zone: overId, slice: q }));
    } else setDropHint(h => (h ? null : h));
  }
  function onDragEnd(e: DragEndEvent) {
    document.body.classList.remove('dnd-dragging');
    setActive(null); setDropHint(null); setPrevPos(null);
    const b = boardRef.current;
    if (!b) return;
    const kind = e.active.data.current?.kind as string | undefined;
    const src = e.active.data.current?.src as string;
    const dest = String(e.over?.id ?? '');
    if (kind === 'counter-add') { if (isCounterZone(dest) && zoneCard(b, dest)) addCounter(dest); return; }
    if (kind === 'counter-move') {
      if (dest === src) return;
      if (isCounterZone(dest) && zoneCard(b, dest)) moveCounter(src, dest); else removeCounter(src);
      return;
    }
    const uid = e.active.data.current?.uid as string;
    if (Math.hypot(e.delta.x, e.delta.y) < 10) { if (isFieldZone(src)) setMenuFor(src); return; }
    if (!dest) return;
    // Dropped on an opponent zone → offer the card to them (with a position for
    // their field zones). Never silently place onto their board.
    if (isOppZone(dest)) {
      if (isOppZone(src)) return;                        // can't move their cards
      const base = baseZone(dest);
      if (base === 'deck') {
        // Their deck: the Shuffle slice offers a face-up "plant"; Top/Bottom
        // offers the card onto their deck. Both go through the give handshake.
        const plant = sliceAt('opp-deck', pointerFrom(e.delta).x, pointerFrom(e.delta).y) === 2;
        startGive(uid, src, 'deck', true, 'atk', plant);
        return;
      }
      let faceUp = true, pos: Pos = 'atk';
      if (isFieldZone(base)) { const p = pointerFrom(e.delta); const q = slicesFor(dest)[sliceAt(dest, p.x, p.y)]; faceUp = q.faceUp; pos = q.pos; }
      startGive(uid, src, base, faceUp, pos);
      return;
    }
    if (dest === 'deck') {
      const q = sliceAt('deck', pointerFrom(e.delta).x, pointerFrom(e.delta).y);
      if (q === 2) shuffleInto(uid, src); else deckMove(uid, src, q === 0);
      return;
    }
    if (isFieldZone(dest)) {
      if (zoneCard(b, dest) && dest !== src) return;
      const p = pointerFrom(e.delta);
      const q = slicesFor(dest)[sliceAt(dest, p.x, p.y)];
      if (dest === src) setPos(dest, q.faceUp, q.pos); else placeCard(uid, src, dest, q.faceUp, q.pos);
    } else if (dest === 'hand' && src === 'deck') searchToHand(uid);   // deck → hand is a search (revealed)
    else if (dest !== src) pileMove(uid, src, dest);
  }
  function onDragCancel() { document.body.classList.remove('dnd-dragging'); setActive(null); setDropHint(null); setPrevPos(null); }

  const menuCard = menuFor && board ? zoneCard(board, menuFor) : null;

  // A read-only field for the opponent (rotated 180° by the container). Face-down
  // and hand/deck cards show cardbacks; hovering hidden cards reveals nothing.
  const oppView = (name: string) => { if (name) setViewed(name); };

  if (loadError) return <div><SiteHeader /><main className="page page--wide"><h1>Duel</h1><p className="error">{loadError}</p><p><Link to="/duel">← Duel</Link></p></main></div>;
  if (!user) return <div><SiteHeader /><main className="page page--wide"><p className="muted">Loading…</p></main></div>;

  const iHaveDeck = !!board;
  const shareLink = `${window.location.origin}/duel/${id}`;

  // ── compact-grid zone content builders ──
  const mb = board ?? emptyBoard;
  const ob = oppBoard ?? emptyBoard;
  const pileBack = (n: number) => (n > 0 ? <div className="play-card play-static"><img src={cardBack} className="play-cardback" alt="" /></div> : null);
  const eyeBtn = (side: 'me' | 'opp', pile: Pile) => (
    <button type="button" className="pt-eye" onClick={e => { e.stopPropagation(); toggleViewer(side, pile); }} title={`View ${PILE_TITLE[pile]}`}>🔍</button>
  );
  // Right-click anywhere on a pile zone (or its 🔍) = clicking its 🔍.
  const viewCtx = (side: 'me' | 'opp', pile: Pile) => (e: React.MouseEvent) => { e.preventDefault(); toggleViewer(side, pile); };
  // Halos are side-scoped (`mine:` / `opp:`) because a card uid can exist on both
  // boards (they're generated per-client), e.g. the skill card's fixed uid.
  const haloCard = (uid: string, mine: boolean) => { flash(`${mine ? 'mine' : 'opp'}:${uid}`); send({ action: 'halo', uid, mine }); };
  const isMon = (zone: string) => zone.startsWith('mon-');
  const myFieldCard = (zone: string) => {
    const c = zoneCard(mb, zone);
    return c ? <PlayCard inst={c} src={zone} faceUp={c.faceUp} pos={c.pos} counters={c.counters ?? 0} atk={c.atk} def={c.def} stats={isMon(zone)} halo={haloed.has(`mine:${c.uid}`)} onView={setViewed} onOpen={setViewed} onHold={() => setMenuFor(zone)} onHalo={() => haloCard(c.uid, true)} onCounterInc={() => addCounter(zone)} onCounterDec={() => removeCounter(zone)} onStatClick={isMon(zone) ? () => setStatEdit({ zone, name: c.name, atk: c.atk, def: c.def }) : undefined} /> : null;
  };
  const myTop = (zone: 'gy' | 'banished') => { const a = mb[zone]; if (!a.length) return null; const c = a[a.length - 1]; return <PlayCard inst={c} src={zone} halo={haloed.has(`mine:${c.uid}`)} onView={setViewed} onOpen={setViewed} />; };
  const mySkill = () => (mb.skill ? <PlayCard inst={mb.skill} src="skill" faceUp={mb.skill.faceUp} pos={mb.skill.pos} halo={haloed.has(`mine:${mb.skill.uid}`)} onView={setViewed} onOpen={setViewed} onHold={() => setMenuFor('skill')} onHalo={() => haloCard(mb.skill!.uid, true)} /> : null);
  const oppFieldCard = (zone: string) => {
    if (pendingGive && pendingGive.dest === zone) {
      const pg = pendingGive;
      return <div className={`play-card play-static duel-pending${pg.pos === 'def' ? ' play-def' : ''}`}>{pg.faceUp ? <OverlayImg name={pg.card.name} /> : <img src={cardBack} className="play-cardback" alt="" />}</div>;
    }
    const c = zoneCard(ob, zone);
    if (!c) return null;
    // Face-up opponent cards are public (name kept); face-down/hidden show a cardback.
    return <PlayCard inst={c} src={`opp-${zone}`} faceUp={c.faceUp && !!c.name} pos={c.pos} counters={c.counters ?? 0} atk={c.atk} def={c.def} stats={isMon(zone) && c.faceUp && !!c.name} draggable={false} halo={haloed.has(`opp:${c.uid}`)} onView={oppView} onHalo={() => haloCard(c.uid, false)} />;
  };
  const oppTop = (zone: 'gy' | 'banished') => { const a = ob[zone]; if (!a.length) return null; const c = a[a.length - 1]; return <PlayCard inst={c} src={`opp-${zone}`} draggable={false} halo={haloed.has(`opp:${c.uid}`)} onView={oppView} />; };
  const oppSkill = () => (ob.skill ? <PlayCard inst={ob.skill} src="opp-skill" faceUp={ob.skill.faceUp} pos={ob.skill.pos} draggable={false} halo={haloed.has(`opp:${ob.skill.uid}`)} onView={oppView} onHalo={() => haloCard(ob.skill!.uid, false)} /> : null);
  // Deck top: a face-up "plant" is public (mine: has the plant flag; opponent's:
  // publicView leaves its name set), otherwise a cardback.
  const deckTop = (b: Board, mine: boolean) => {
    const top = b.deck[0];
    if (!top) return null;
    const faceUp = mine ? !!top.plant : !!top.name;
    return faceUp
      ? <div className="play-card play-static duel-plant-top" onClick={mine ? undefined : () => setViewed(top.name)}><OverlayImg name={top.name} /></div>
      : <div className="play-card play-static"><img src={cardBack} className="play-cardback" alt="" /></div>;
  };
  const gpos = (col: number | string, row: number): React.CSSProperties => ({ gridColumn: col, gridRow: `${row} / span 2` });

  return (
    <div>
      <SiteHeader />
      <main className="page playtest-layout duel-layout">
        {/* Left: persistent card preview */}
        <aside className="pt-left">
          <div className="pt-preview"><CardPreview name={viewed} /></div>
        </aside>

        <DndContext sensors={sensors} collisionDetection={pointerWithin} autoScroll={false} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <div className="pt-board duel-board">
          {status !== 'active' && !iHaveDeck && (
            <div className="duel-lobby">
              <h2>Duel lobby</h2>
              <div className="duel-share-row">
                <span className="muted">Invite link:</span>
                <span className="duel-share">{shareLink}</span>
                <button type="button" className="duel-copy-btn" onClick={copyLink}>{copied ? '✓ Copied' : 'Copy link'}</button>
              </div>
              <div className="duel-roster">
                {players.map(p => (
                  <div key={p.userId} className="duel-roster-row">
                    <span className={`duel-dot${p.connected ? ' on' : ''}`} />
                    <span>{p.displayName}{p.userId === yourId ? ' (you)' : ''}</span>
                    <span className="muted">{p.hasDeck ? `deck: ${p.deckName ?? 'ready'}` : 'choosing…'}</span>
                  </div>
                ))}
                {players.length < 2 && <p className="muted">Waiting for an opponent to join…</p>}
              </div>

              {chosenDeckId ? (
                <div className="duel-deck-done">
                  <strong>✓ Deck locked in.</strong> Waiting for {players.length < 2 ? 'an opponent' : 'your opponent to pick a deck'}…
                </div>
              ) : (
                <>
                  <div className="duel-pick-callout">Select a deck to start the duel</div>
                  {!myDecks && <p className="muted">Loading your decks…</p>}
                  {myDecks && myDecks.length === 0 && <p className="muted">You have no decks yet — build one on the <Link to="/decks">Decks</Link> page first.</p>}
                  <div className="deck-list duel-deck-list">
                    {myDecks?.map(d => (
                      <DuelDeckPick key={d.id} deck={d} onPick={chooseDeck} getToken={getToken} refresh={refresh} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {(status === 'active' || iHaveDeck) && (
            <>
              {/* One 5-column grid; side columns staggered a half-card. Opponent
                  zones (opp- prefixed, rotated 180°) and mine interleave. Center
                  column shows the turn / the pile viewer. */}
              <div className="duel-grid">
                {/* ── Opponent (top; each slot rotated 180°) ── */}
                <div className="duel-hand-slot duel-opp-slot" style={{ gridColumn: '2 / 5', gridRow: '1 / 3' }}>
                  <div className="pt-hand duel-hand-inner"><span className="pt-zone-label">Opp hand ({ob.hand.length})</span>
                    <div className="pt-hand-cards">{ob.hand.map(c => <div key={c.uid} className="play-card play-static"><img src={cardBack} className="play-cardback" alt="" /></div>)}</div>
                  </div>
                </div>
                {[0, 1, 2].map(i => <div key={`ost${i}`} className="duel-slot duel-opp-slot" style={gpos(i + 2, 3)}><Zone id={`opp-st-${i}`} className="pt-zone-st" label="S/T">{oppFieldCard(`st-${i}`)}</Zone></div>)}
                {[0, 1, 2].map(i => <div key={`omon${i}`} className="duel-slot duel-opp-slot" style={gpos(i + 2, 5)}><Zone id={`opp-mon-${i}`} className="pt-zone-mon" label="Mon">{oppFieldCard(`mon-${i}`)}</Zone></div>)}
                <div className="duel-slot duel-opp-slot" style={gpos(1, 2)}><Zone id="opp-deck" className="pt-zone-pile" label={`Deck ${ob.deck.length}`}>{deckTop(ob, false)}{oppShuffleTick > 0 && <ShuffleFx key={oppShuffleTick} />}{oppBoard && <DotMenu items={oppDeckMenu} />}</Zone></div>
                <div className="duel-slot duel-opp-slot" style={gpos(1, 4)}><Zone id="opp-gy" className="pt-zone-pile" label={`GY ${ob.gy.length}`} onContext={viewCtx('opp', 'gy')}>{oppTop('gy')}{eyeBtn('opp', 'gy')}</Zone></div>
                <div className="duel-slot duel-opp-slot" style={gpos(1, 6)}><Zone id="opp-banished" className="pt-zone-pile" label={`Ban ${ob.banished.length}`} onContext={viewCtx('opp', 'banished')}>{oppTop('banished')}{eyeBtn('opp', 'banished')}</Zone></div>
                <div className="duel-slot duel-opp-slot" style={gpos(5, 2)}><Zone id="opp-extra" className="pt-zone-pile" label={`Extra ${ob.extra.length}`}>{pileBack(ob.extra.length)}</Zone></div>
                <div className="duel-slot duel-opp-slot" style={gpos(5, 4)}><Zone id="opp-fieldspell" className="pt-zone-field" label="Field">{oppFieldCard('fieldspell')}</Zone></div>
                <div className="duel-slot duel-opp-slot" style={gpos(5, 6)}><Zone id="opp-skill" className="pt-zone-field" label="Skill">{oppSkill()}</Zone></div>

                {/* ── Center (stat editor / deck peek / pile viewer / turn) ── */}
                <div className="duel-center-cell" style={{ gridColumn: '2 / 5', gridRow: '7 / 9' }}>
                  {statEdit ? (
                    <StatEditor name={statEdit.name} atk={statEdit.atk} def={statEdit.def}
                      onConfirm={(a, d) => { setStats(statEdit.zone, a, d); setStatEdit(null); }} onCancel={() => setStatEdit(null)} />
                  ) : deckPeek ? (
                    <DeckPeekViewer cards={deckPeek.cards} reveal={deckPeek.reveal} onReorder={peekReorder} onClose={peekClose} onView={setViewed} />
                  ) : revealSel ? (
                    <RevealSelectViewer cards={mb.deck} n={revealSel.n} selected={revealSel.picked}
                      onToggle={toggleRevealPick} onConfirm={confirmRevealAny} onCancel={closeRevealAny} onView={setViewed} />
                  ) : oppLook ? (
                    <div className="duel-viewer-inline">
                      <div className="pt-viewer-head">
                        <h4>{oppLook.reveal ? 'Revealed' : 'Looking at'} {opp?.displayName ?? 'Opp'} top {oppLook.cards.length}</h4>
                        <button type="button" className="preview-close" onClick={() => setOppLook(null)} title="Close">✕</button>
                      </div>
                      {oppLook.cards.length === 0 ? <p className="muted">Empty.</p>
                        : <ViewerStrip cards={oppLook.cards} src="opplook" draggable={false} onView={setViewed} />}
                    </div>
                  ) : viewer ? (
                    <div className="duel-viewer-inline">
                      <div className="pt-viewer-head">
                        <h4>{viewer.side === 'opp' ? 'Opp ' : ''}{PILE_TITLE[viewer.pile]} ({viewerCards.length})</h4>
                        <button type="button" className="preview-close" onClick={() => changeViewer(null)} title="Close">✕</button>
                      </div>
                      {viewerCards.length === 0 ? <p className="muted">Empty.</p>
                        : <ViewerStrip cards={viewerCards} src={viewer.pile} draggable={viewer.side === 'me'} onView={setViewed}
                            onToHand={viewer.side === 'me' ? uid => { if (viewer.pile === 'deck') { searchToHand(uid); changeViewer(null); } else pileMove(uid, viewer.pile, 'hand'); } : undefined} />}
                    </div>
                  ) : (
                    <div className={`duel-turn${myTurn ? ' mine' : ''}${status === 'ended' ? (endedBy === yourId ? ' duel-defeat' : ' duel-victory') : ''}`}>
                      {status === 'ended'
                        ? (endedBy === yourId ? 'Defeat' : 'Victory')
                        : turn ? (myTurn ? 'Your turn' : `${opp?.displayName ?? 'Opponent'}'s turn`) : 'Waiting…'}
                    </div>
                  )}
                </div>

                {/* ── Me (bottom; interactive) ── */}
                {[0, 1, 2].map(i => <div key={`mmon${i}`} className="duel-slot" style={gpos(i + 2, 9)}><Zone id={`mon-${i}`} className="pt-zone-mon" label="Mon">{myFieldCard(`mon-${i}`)}</Zone></div>)}
                {[0, 1, 2].map(i => <div key={`mst${i}`} className="duel-slot" style={gpos(i + 2, 11)}><Zone id={`st-${i}`} className="pt-zone-st" label="S/T">{myFieldCard(`st-${i}`)}</Zone></div>)}
                <div className="duel-hand-slot" style={{ gridColumn: '2 / 5', gridRow: '13 / 15' }}>
                  <HandZone hand={mb.hand} menu={<DotMenu items={handMenu} up />} halo={uid => haloed.has(`mine:${uid}`)} onView={setViewed} onOpen={setViewed}
                    picking={revealPick} onPick={inst => { setRevealPick(false); setRevealConfirm(inst); }} />
                </div>
                <div className="duel-slot" style={gpos(1, 8)}><Zone id="skill" className="pt-zone-field" label="Skill">{mySkill()}</Zone></div>
                <div className="duel-slot" style={gpos(1, 10)}><Zone id="fieldspell" className="pt-zone-field" label="Field">{myFieldCard('fieldspell')}</Zone></div>
                <div className="duel-slot" style={gpos(1, 12)}><Zone id="extra" className="pt-zone-pile" label={`Extra ${mb.extra.length}`} onContext={viewCtx('me', 'extra')}>{pileBack(mb.extra.length)}{eyeBtn('me', 'extra')}</Zone></div>
                <div className="duel-slot" style={gpos(5, 8)}><Zone id="banished" className="pt-zone-pile" label={`Ban ${mb.banished.length}`} onContext={viewCtx('me', 'banished')}>{myTop('banished')}{eyeBtn('me', 'banished')}</Zone></div>
                <div className="duel-slot" style={gpos(5, 10)}><Zone id="gy" className="pt-zone-pile" label={`GY ${mb.gy.length}`} onContext={viewCtx('me', 'gy')}>{myTop('gy')}{eyeBtn('me', 'gy')}</Zone></div>
                <div className="duel-slot" style={gpos(5, 12)}><Zone id="deck" className="pt-zone-pile" label={`Deck ${mb.deck.length}`} onClick={draw} onContext={viewCtx('me', 'deck')}>{deckTop(mb, true)}{shuffleTick > 0 && <ShuffleFx key={shuffleTick} />}<DotMenu items={deckMenu} up />{eyeBtn('me', 'deck')}</Zone></div>
              </div>

              <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
                {active?.kind === 'counter' ? <div className="play-counter play-counter-drag">●</div>
                  : active?.kind === 'card' ? (() => {
                    const isDeck = dropHint ? baseZone(dropHint.zone) === 'deck' : false;
                    const hp = dropHint && !isDeck ? slicesFor(dropHint.zone)[dropHint.slice] : null;
                    return <div className={`play-card drag-overlay${hp?.pos === 'def' ? ' play-def' : ''}`}>
                      {hp && !hp.faceUp ? <img src={cardBack} className="play-cardback" alt="" /> : <OverlayImg name={active.name!} />}
                    </div>;
                  })() : null}
              </DragOverlay>
              {dropHint && <SliceOverlay hint={dropHint} prevSlice={prevPos && prevPos.zone === dropHint.zone ? prevPos.slice : -1} />}

              {/* Cards the opponent has revealed to me (auto-fades after 4s) */}
              {revealVisible && oppRevealed.length > 0 && (
                <div className="duel-reveal-strip" key={revealNonce}>
                  <span className="duel-reveal-label">{opp?.displayName ?? 'Opponent'} revealed:</span>
                  {oppRevealed.map(c => <div key={c.uid} className="duel-reveal-card"><PlayCard inst={c} src="reveal" draggable={false} onView={setViewed} /></div>)}
                </div>
              )}

            </>
          )}
        </div>

        {/* Right: controls */}
        <aside className="pt-controls">
          <div className="pt-lp-panel">
            <div className="pt-lp-row"><span>Opp</span><strong className="pt-lp-val">{shownOppLp}</strong></div>
            <div className="pt-lp-row"><span>You</span><strong className="pt-lp-val">{shownMyLp}</strong>
              <button type="button" onClick={() => applyLp(1)} aria-label="add to your LP">+</button>
              <button type="button" onClick={() => applyLp(-1)} aria-label="subtract from your LP">−</button>
            </div>
            <input type="number" inputMode="numeric" className="pt-lp-input" placeholder="Amount" value={lpAmt} onChange={e => setLpAmt(e.target.value)} />
          </div>
          <div className="pt-tools">
            <button type="button" className="pt-tool" onClick={rollDice} disabled={diceState !== 'idle'}>
              <span className="pt-tool-label">🎲 Dice</span>
              <span className={`pt-tool-result${dice == null ? ' placeholder' : ''}${diceState === 'rolling' ? ' rolling' : ''}`}>{dice ?? 'Roll'}</span>
            </button>
            <button type="button" className="pt-tool" onClick={flipCoin} disabled={coinState !== 'idle'}>
              <span className="pt-tool-label">🪙 Coin</span>
              <span className={`pt-tool-result${coin == null ? ' placeholder' : ''}${coinState === 'rolling' ? ' rolling' : ''}`}>{coin ?? 'Flip'}</span>
            </button>
          </div>
          <div className="pt-game-btns">
            <button type="button" onClick={draw} disabled={!board?.deck.length || !!deckPeek}>Draw ({board?.deck.length ?? 0})</button>
            <button type="button" onClick={excavate} disabled={!board?.deck.length || !!deckPeek}>Excavate</button>
            <button type="button" onClick={shuffleDeck}>Shuffle</button>
            <button type="button" onClick={endTurn} disabled={!myTurn || status !== 'active'}>End turn</button>
          </div>
          <CounterSource />
          {status === 'ended'
            ? <button type="button" className="create-deck-btn" disabled={rematchSent} onClick={() => { send({ action: 'rematch' }); setRematchSent(true); }}>{rematchSent ? 'Waiting for opponent…' : 'Rematch'}</button>
            : <button type="button" className="btn-danger" onClick={() => setConfirmConcede(true)} disabled={status !== 'active'}>Concede</button>}
          <button type="button" className="pt-back-btn" onClick={() => navigate('/duel')}>← Leave</button>
          {error && <p className="error">{error}</p>}

          {board && (
            <div className="duel-log">
              <div className="duel-log-title">Match Log</div>
              <div className="duel-log-scroll" ref={logScrollRef}>
                {matchLog.length === 0
                  ? <p className="muted duel-log-empty">No actions yet.</p>
                  : matchLog.map(e => (
                    <div key={e.id} className="duel-log-entry">
                      {e.segs.map((s, i) => typeof s === 'string'
                        ? <span key={i}>{s}</span>
                        : <span key={i} className="duel-log-card" onMouseEnter={() => setViewed(s.card)}>{s.card}</span>)}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </aside>
        </DndContext>
      </main>

      {/* Position menu */}
      {menuFor && menuCard && (
        <div className="pt-popup-backdrop" onClick={() => setMenuFor(null)}>
          <div className="pt-popup pt-pos-popup" style={anchorStyle(menuFor)} onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">{menuCard.name}</p>
            <div className="pt-pos-grid">
              {slicesFor(menuFor).map(o => (
                <PosOption key={o.label} name={menuCard.name} faceUp={o.faceUp} pos={o.pos} label={o.label}
                  onClick={() => { setPos(menuFor, o.faceUp, o.pos); setMenuFor(null); }} />
              ))}
            </div>
            <div className="pt-pos-actions">
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'gy'); setMenuFor(null); }}>Send to Graveyard</button>
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'banished'); setMenuFor(null); }}>Banish</button>
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'hand'); setMenuFor(null); }}>Return to Hand</button>
            </div>
            <button type="button" className="duel-plant-btn" onClick={() => { const z = menuFor; const c = menuCard; setMenuFor(null); startGive(c.uid, z, 'deck', true, 'atk', true); }}>Shuffle face-up into opponent's deck</button>
            <button type="button" className="pt-popup-cancel" onClick={() => setMenuFor(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Give a card to the opponent — my confirm, then wait for them */}
      {pendingGive && (
        <div className="pt-popup-backdrop" onClick={pendingGive.stage === 'mine' ? cancelGive : undefined}>
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            {pendingGive.stage === 'mine' ? (
              <>
                <p className="pt-popup-title">Give this card to your opponent?</p>
                <p className="pt-confirm-text muted">{pendingGive.card.name} → their {ZONE_LABEL(pendingGive.dest)}. They must accept it.</p>
                <div className="pt-pos-actions">
                  <button type="button" className="create-deck-btn" onClick={confirmGive}>Give</button>
                  <button type="button" onClick={cancelGive}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p className="pt-popup-title">Waiting for opponent…</p>
                <p className="pt-confirm-text muted">Offered {pendingGive.card.name} to their {ZONE_LABEL(pendingGive.dest)}.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* "Reveal card in hand": non-blocking prompt while you pick one */}
      {revealPick && (
        <div className="duel-pick-banner">
          <span>Click a card in your hand to reveal it to your opponent.</span>
          <button type="button" onClick={() => setRevealPick(false)}>Cancel</button>
        </div>
      )}

      {/* Double-check before the reveal actually goes out */}
      {revealConfirm && (
        <div className="pt-popup-backdrop" onClick={() => setRevealConfirm(null)}>
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Reveal this card to your opponent?</p>
            <p className="pt-confirm-text muted">{revealConfirm.name} will be shown to {opp?.displayName ?? 'your opponent'}.</p>
            <div className="pt-pos-actions">
              <button type="button" className="create-deck-btn" onClick={() => { reveal([revealConfirm.uid]); setRevealConfirm(null); }}>Reveal</button>
              <button type="button" onClick={() => setRevealConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* "How many?" custom popup (deck ⋯ actions) */}
      {numPrompt && (
        <NumberPrompt title={numPrompt.title} max={numPrompt.max}
          onSubmit={n => { const cb = numPrompt.onSubmit; setNumPrompt(null); cb(n); }} onCancel={() => setNumPrompt(null)} />
      )}

      {/* Opponent wants to look at / reveal the top of MY deck — I confirm */}
      {incomingDeckReq && (
        <div className="pt-popup-backdrop">
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">{opp?.displayName ?? 'Your opponent'} wants to {incomingDeckReq.kind === 'reveal' ? 'reveal' : 'look at'} your deck</p>
            <p className="pt-confirm-text muted">Allow them to {incomingDeckReq.kind === 'reveal' ? 'reveal' : 'privately look at'} the top {incomingDeckReq.n} card{incomingDeckReq.n === 1 ? '' : 's'} of your deck?</p>
            <div className="pt-pos-actions">
              <button type="button" className="create-deck-btn" onClick={acceptDeckReq}>Allow</button>
              <button type="button" className="btn-danger" onClick={declineDeckReq}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Incoming give from the opponent */}
      {incomingGive && (
        <div className="pt-popup-backdrop">
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Accept a card from your opponent?</p>
            <p className="pt-confirm-text muted">{incomingGive.name} → your {ZONE_LABEL(incomingGive.zone)}. Auto-declines in {giveCountdown}s.</p>
            <div className="pt-pos-actions">
              <button type="button" className="create-deck-btn" onClick={acceptIncoming}>Accept</button>
              <button type="button" className="btn-danger" onClick={declineIncoming}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Rematch offer from the opponent */}
      {rematchOffer && (
        <div className="pt-popup-backdrop">
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Rematch?</p>
            <p className="pt-confirm-text muted">{opp?.displayName ?? 'Your opponent'} wants a rematch — same room, fresh game.</p>
            <div className="pt-pos-actions">
              <button type="button" className="create-deck-btn" onClick={() => { setRematchOffer(false); send({ action: 'rematch' }); }}>Accept</button>
              <button type="button" className="btn-danger" onClick={() => { setRematchOffer(false); send({ action: 'rematchDecline' }); navigate('/duel'); }}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Concede confirmation */}
      {confirmConcede && (
        <div className="pt-popup-backdrop" onClick={() => setConfirmConcede(false)}>
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Concede the duel?</p>
            <p className="pt-confirm-text muted">Your opponent wins this game.</p>
            <div className="pt-pos-actions">
              <button type="button" className="btn-danger" onClick={concede}>Concede</button>
              <button type="button" onClick={() => setConfirmConcede(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A duel deck-picker tile: cover icon + name + legality status (Legal / Draft /
 *  Illegal), matching the Decks page. Fetches the full deck once to classify it. */
function DuelDeckPick({ deck, onPick, getToken, refresh }: {
  deck: DeckSummary; onPick: (id: string) => void;
  getToken: () => string | null; refresh: () => Promise<boolean>;
}) {
  const image = useCardImage(deck.coverCard);
  const [legality, setLegality] = useState<Legality | null>(null);
  useEffect(() => {
    let cancelled = false;
    decksApi.get(deck.id, getToken, refresh).then(async full => {
      const names = [full.skill, ...full.mainDeck, ...full.extraDeck, ...full.sideDeck]
        .map(c => (typeof c === 'string' ? c : c?.name)).filter(Boolean) as string[];
      await Promise.all(names.map(n => ensureMeta(n)));
      if (cancelled) return;
      setLegality(computeLegality(full.skill, { main: full.mainDeck, extra: full.extraDeck, side: full.sideDeck, considering: full.considering }, getMeta));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [deck.id, getToken, refresh]);
  const cls = legality ? (legality.status === 'Legal' ? 'legal' : legality.status === 'Draft' ? 'draft' : 'illegal') : '';
  return (
    <button type="button" className="deck-row duel-deck-row" onClick={() => onPick(deck.id)}>
      <div className="deck-row-cover"><CardImage src={cardImageUrl(image)} alt={deck.title} /></div>
      <div className="deck-row-body">
        <div className="deck-row-title">{deck.title}</div>
        <div className="deck-row-meta muted">
          {legality
            ? <span className={`status-badge duel-status-badge status-${cls}`}><span className="status-dot" /> {legality.status}</span>
            : <span className="duel-status-loading">Checking…</span>}
          <span>Main {deck.counts.main}</span>
          {deck.counts.extra > 0 && <span>· Extra {deck.counts.extra}</span>}
        </div>
      </div>
    </button>
  );
}

const PILE_TITLE: Record<string, string> = { deck: 'Deck', gy: 'Graveyard', extra: 'Extra Deck', banished: 'Banished' };

const ZONE_NAMES: Record<string, string> = { fieldspell: 'Field zone', hand: 'hand', gy: 'Graveyard', deck: 'deck', extra: 'Extra deck', banished: 'Banished', skill: 'Skill zone' };
function ZONE_LABEL(zone: string): string {
  if (zone.startsWith('mon-')) return 'Monster zone';
  if (zone.startsWith('st-')) return 'Spell/Trap zone';
  return ZONE_NAMES[zone] ?? zone;
}

type Pile = 'deck' | 'gy' | 'extra' | 'banished';

const emptyBoard: Board = { deck: [], hand: [], monsters: [null, null, null], spells: [null, null, null], fieldSpell: null, gy: [], extra: [], banished: [], skill: null };
